/**
 * Error boundary component.
 * Catches React errors and displays a styled error message.
 */

import { Component, type ReactNode } from 'react';
import { AlertCircle } from '@core/components/icons';

/**
 * Error boundary props
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Error boundary state
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component to catch React errors.
 * Displays a styled error message when an error occurs.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-screen p-6 bg-[var(--mui-palette-background-default)]">
          <div className="max-w-[600px] w-full rounded-lg border border-[var(--glass-border)] bg-[var(--mui-palette-background-paper)] shadow-lg">
            <div className="p-12 text-center">
              <div className="mb-8 text-red-500 leading-none">
                <AlertCircle size={88} />
              </div>
              <h4 className="text-2xl font-semibold mb-4">
                Something went wrong
              </h4>
              <p className="text-base text-[var(--mui-palette-text-secondary)] mb-8">
                {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
              </p>
              <button
                type="button"
                onClick={() => this.setState({ hasError: false, error: null })}
                className="inline-flex items-center px-6 py-2 rounded-md bg-[var(--mui-palette-primary-main)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
